import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../images/logo.jpg'; 

const NavBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Handle Scroll
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpen]);

  // THEME CONFIG
  const navLinks = [
    { 
      name: "About Us", 
      path: "/about", 
      hoverClass: "hover:bg-cyan-50 hover:border-[#06B6D4] hover:shadow-[3px_3px_0px_#06B6D4]", 
      activeClass: "bg-cyan-50 border-[#06B6D4] text-primary-navy shadow-none translate-y-[2px] translate-x-[2px]", 
      mobileShadow: "shadow-[4px_4px_0px_#06B6D4]"
    },
    { 
      name: "Programs", 
      path: "/programs", 
      hoverClass: "hover:bg-orange-50 hover:border-[#FF5E1E] hover:shadow-[3px_3px_0px_#FF5E1E]", 
      activeClass: "bg-orange-50 border-[#FF5E1E] text-primary-navy shadow-none translate-y-[2px] translate-x-[2px]",
      mobileShadow: "shadow-[4px_4px_0px_#FF5E1E]"
    },
    { 
      name: "Events", 
      path: "/events", 
      hoverClass: "hover:bg-purple-50 hover:border-[#9D4EDD] hover:shadow-[3px_3px_0px_#9D4EDD]", 
      activeClass: "bg-purple-50 border-[#9D4EDD] text-primary-navy shadow-none translate-y-[2px] translate-x-[2px]",
      mobileShadow: "shadow-[4px_4px_0px_#9D4EDD]"
    },
    { 
      name: "Resources", 
      path: "/resources", 
      hoverClass: "hover:bg-lime-50 hover:border-[#CCFF00] hover:shadow-[3px_3px_0px_#CCFF00]", 
      activeClass: "bg-lime-50 border-[#CCFF00] text-primary-navy shadow-none translate-y-[2px] translate-x-[2px]",
      mobileShadow: "shadow-[4px_4px_0px_#CCFF00]"
    },
  ];

  return (
    <>
      {/* BACKDROP */}
      <div 
        className={`
          fixed inset-0 bg-primary-navy/20 backdrop-blur-sm z-40 transition-opacity duration-300
          ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}
        `}
        onClick={() => setIsOpen(false)}
      />

      <nav 
        className={`
          fixed top-4 inset-x-4 z-50 
          transition-all duration-300 ease-out
          rounded-full 
          border-[3px] border-primary-navy 
          ${scrolled || isOpen ? 'bg-white shadow-[4px_4px_0px_#020617] py-2' : 'bg-white shadow-[0px_0px_0px_#020617] py-3'}
          max-w-7xl mx-auto
        `}
      >
        <div className="px-4 md:px-6 flex justify-between items-center relative">
          
          {/* LOGO */}
          <Link to="/" className="flex items-center gap-3 group relative z-50" onClick={() => setIsOpen(false)}>
            <img 
              src={logo} 
              alt="Positive Somebody Logo" 
              className="h-10 md:h-12 rounded-full border-2 border-transparent transition-all duration-300 group-hover:rotate-12" 
            />
            <span className="font-heading font-bold text-xl md:text-2xl text-[#06B6D4] tracking-tight">
              <span className="text-primary-navy">Positive</span> <span className="text-[#3B82F6]">Somebody</span>
            </span>
          </Link>

          {/* DESKTOP LINKS */}
          <div className="hidden lg:flex items-center gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`
                  px-5 py-2 rounded-full font-heading font-bold text-sm tracking-wide transition-all duration-200 border-2
                  ${location.pathname === link.path 
                    ? link.activeClass 
                    : `bg-transparent text-primary-navy border-transparent ${link.hoverClass} hover:-translate-y-0.5 hover:-rotate-1`
                  }
                `}
              >
                {link.name}
              </Link>
            ))}

            <a 
              href="https://stores.inksoft.com/positive_somebody/shop/home"
              target="_blank" 
              rel="noopener noreferrer"
              className="px-5 py-2 rounded-full font-heading font-bold text-sm text-primary-navy border-2 border-dashed border-gray-300 hover:border-accent-purple hover:text-accent-purple hover:bg-purple-50 transition-all flex items-center gap-1 ml-2 group"
            >
              Shop <span className="group-hover:-translate-y-1 group-hover:translate-x-1 transition-transform">↗</span>
            </a>
          </div>

          {/* ACTION BUTTONS */}
          <div className="flex items-center gap-3">
              
            {/* DESKTOP DONATE - Givebutter Widget */}
            <div className="hidden sm:block">
              <givebutter-widget id="gO8aYp"></givebutter-widget>
            </div>

            {/* TOGGLE BUTTON */}
            <button 
              onClick={() => setIsOpen(!isOpen)} 
              className={`
                lg:hidden w-12 h-12 flex items-center justify-center rounded-full transition-all duration-200 border-[3px] relative z-50
                ${isOpen 
                  ? 'bg-accent-yellow border-primary-navy text-primary-navy shadow-none translate-y-[2px] translate-x-[2px]' 
                  : 'bg-white border-primary-navy text-primary-navy shadow-[3px_3px_0px_#020617] active:shadow-none active:translate-y-[2px] active:translate-x-[2px]'}
              `}
              aria-label="Toggle Menu"
            >
              {isOpen ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* --- MOBILE MENU DROPDOWN --- */}
        <div 
          className={`
            absolute top-[calc(100%+16px)] left-0 right-0 
            bg-white rounded-[2rem] border-[3px] border-primary-navy 
            shadow-[8px_8px_0px_#020617] 
            p-5 flex flex-col gap-3 
            transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] origin-top z-40
            ${isOpen ? 'opacity-100 scale-100 translate-y-0 visible' : 'opacity-0 scale-95 -translate-y-4 invisible'}
          `}
        >
          {/* MOBILE NAV ITEMS */}
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              className={`
                 w-full text-lg font-heading font-bold py-4 px-6 rounded-full border-[3px] transition-all duration-150
                 flex items-center justify-between group
                 ${location.pathname === link.path 
                    ? link.activeClass 
                    : `bg-white text-primary-navy border-primary-navy ${link.mobileShadow} active:shadow-none active:translate-x-[2px] active:translate-y-[2px]`}
              `}
              onClick={() => setIsOpen(false)}
            >
              <span>{link.name}</span>
              <span className={`transition-transform text-2xl ${location.pathname === link.path ? 'rotate-0 opacity-100' : '-rotate-45 opacity-50 group-active:rotate-0'}`}>
                ➜
              </span>
            </Link>
          ))}
          
          {/* MOBILE SHOP LINK */}
          <a 
              href="https://stores.inksoft.com/positive_somebody/shop/home"
              target="_blank" 
              rel="noopener noreferrer"
              className="w-full text-lg font-heading font-bold text-primary-navy py-4 px-6 rounded-full border-[3px] border-dashed border-gray-300 hover:border-accent-purple hover:bg-purple-50 hover:text-accent-purple active:scale-[0.98] transition-all flex items-center justify-between mt-2"
          >
              <span>Shop</span>
              <span>↗</span>
          </a>

          {/* MOBILE DONATE - Givebutter Widget */}
          <div className="mt-2 w-full flex justify-center">
            <givebutter-widget id="gO8aYp"></givebutter-widget>
          </div>
        </div>
      </nav>
    </>
  );
};

export default NavBar;